import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Box, Button, FormHelperText, Stack, Typography } from '@mui/material';
import CustomTextField from 'src/components/forms/theme-elements/CustomTextField';
import { ErrorInterface, ValidationStateSchema } from 'src/interfaces/Form';
import { Trans, useTranslation } from 'react-i18next';
import ValidationStateSchemaHelper from 'src/helpers/ValidationStateSchemaHelper';
import { LoadingScreenContext } from 'src/context/LoadingScreenContext';
import { setToken } from 'src/helpers/TokenHelper';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthService } from 'src/api/apiMms';

interface Props {
  title?: string;
  subtitle?: string;
  subtext?: string;
}

interface State {
  username: string;
  password: string;
}

const validationStateSchema: ValidationStateSchema = {
  username: {
    required: true,
    validator: null,
    error: 'required',
  },
  password: {
    required: true,
    validator: null,
    error: 'required',
  },
};

const AuthLogin: React.FC<Props> = ({ title, subtitle, subtext }) => {
  const { t } = useTranslation();
  
  const translate = (key: string, context?: Record<string, string>) =>
    t(`views.login.${key}`, context);

  const loadingScreenContext = useContext(LoadingScreenContext);

  const navigate = useNavigate();
  const useSearchParams = () => {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  };
  const searchParams = useSearchParams();

  const [state, setState] = useState<State>({ username: '', password: '' });
  const [errors, setErrors] = useState<ErrorInterface>({});
  const [error, setError] = useState('');

  const validateState = useCallback(() => {
    setError('');
    let hasErrorInState = false;
    const errors: ErrorInterface = {};
    Object.keys(validationStateSchema).forEach((key) => {
      const isInputFieldRequired: boolean = validationStateSchema[key].required;

      // @ts-ignore
      if (isInputFieldRequired && !state[key]) {
        hasErrorInState = true;
        errors[key] = translate(`errors.${validationStateSchema[key].error}`);
      }
    });

    setErrors(errors);

    return !hasErrorInState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const handleOnChange = (field: string, value: any) => {
    setState((prevState) => ({ ...prevState, [field]: value }));
  };

  const handleSignIn = () => {
    loadingScreenContext.setLoading(true);

    AuthService.login({
      username: state.username,
      password: state.password,
    })
      .then(
        (response) => {
          if (response && response.token) {
            setToken(response.token);
            sessionStorage.setItem('username', state.username);
            loadingScreenContext.setLoading(false);
            redirect();
          } else {
            setError(response.message || '');
            loadingScreenContext.setLoading(false);
          }
        },
        (error) => {
          loadingScreenContext.setLoading(false);
          console.warn('Error during login attempt:', error);
        }
      )
      .catch((error: any) => {
        loadingScreenContext.setLoading(false);
        console.warn('Something went wrong:', error);
      });
  };

  const handleOnSubmit = (event: any) => {
    event.preventDefault();

    if (validateState()) {
      handleSignIn();
    }
  };

  const redirect = () => {
    const currentPath = searchParams.get('currentPath');

    if (currentPath) {
      navigate(`/${currentPath}`, { replace: true });
    } else {
      navigate(`/dashboard`, { replace: true });
    }
  };

  return (
    <>
      {title ? (
        <Typography fontWeight="700" variant="h2" mb={1}>
          {title}
        </Typography>
      ) : null}

      {subtext}

      <Stack>
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            component="label"
            htmlFor="username"
            mb="5px">
            <Trans i18nKey="views.login.fields.username">Username</Trans>
          </Typography>
          <CustomTextField
            id="username"
            variant="outlined"
            fullWidth
            value={state.username}
            onChange={(e) => {
              handleOnChange('username', e.target.value);
            }}
            error={!!errors['username']}
            helperText={errors['username']}
            required={ValidationStateSchemaHelper.isRequired(
              validationStateSchema,
              'username'
            )}
          />
        </Box>
        <Box my={2}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            component="label"
            htmlFor="password"
            mb="5px">
            <Trans i18nKey="views.login.fields.password">Password</Trans>
          </Typography>
          <CustomTextField
            id="password"
            type="password"
            variant="outlined"
            fullWidth
            value={state.password}
            onChange={(e) => {
              handleOnChange('password', e.target.value);
            }}
            error={!!errors['password']}
            helperText={errors['password']}
            required={ValidationStateSchemaHelper.isRequired(
              validationStateSchema,
              'password'
            )}
          />
        </Box>
      </Stack>
      {error && (
        <FormHelperText error style={{ margin: '10px', textAlign: 'center' }}>
          {error}
        </FormHelperText>
      )}
      <Box>
        <Button
          color="primary"
          variant="contained"
          size="large"
          fullWidth
          type="submit"
          onClick={handleOnSubmit}>
          <Trans i18nKey="views.login.buttons.signIn">Login</Trans>
        </Button>
      </Box>
      {subtitle}
    </>
  );
};

export default AuthLogin;
